import {ServiceBase} from '../ServiceBase';
import {AiRecommendedGames, IAiEngineService, IAiSortOptions} from './ai-engine.service.interface';
import { action, observable, makeObservable } from 'mobx';
import {SEO_CATEGORIES} from '../../consts/seo-categories';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {Check} from '@sparkware/app-utils';
import {LIST_TYPE_ID} from "../../../common/const/ai-trending-list-ids-map";

export const NO_AI_GAMES_RECOMMENDATIONS: AiRecommendedGames = {
    isInitialized: false,
    gameTypes: [],
    gameTypesMap: {},
    engine: undefined
};

interface ITrendingGamesRecommendations {
  gamesByBets: number[];
  gamesByActiveUsers: number[];
}

export class AiEngineService extends ServiceBase implements IAiEngineService {
  constructor(services: IServiceFactoryExtended){
    super(services);

    makeObservable(this);

    this.reaction(() => this.services.user.isAuthorized,
      () => this._onOauthChange(),
      {
        fireImmediately: true
      });

    this.services.sdk.events.onGamesQuestionnaireCompleted(async () => {
      await this._updateAiRecommendedGames();
    });
  }

  @observable.ref
  aiRecommendedGames: AiRecommendedGames = NO_AI_GAMES_RECOMMENDATIONS;

  @observable.ref
  _trendingGamesRecommendations: ITrendingGamesRecommendations | null = null;
  get trendingRecommendedGamesForPlayerByBets(): number[] | null {
    this._loadTrendingRecommendedGamesForPlayer();
    return this._trendingGamesRecommendations?.gamesByBets || null;
  }
  get trendingRecommendedGamesForPlayerByActiveUsers(): number[] | null {
    this._loadTrendingRecommendedGamesForPlayer();
    return this._trendingGamesRecommendations?.gamesByActiveUsers || null;
  }

  /**
   * @description: A map where the key is the game type and the value is then game position in the AI recommendation
   */
  @observable.ref
  private _allAiRecommendedGameTypes: Record<number, number>;


  @action.bound
  private _setAiRecommendedGames(value: AiRecommendedGames) {

    let tempGameTypesMap = {};

    if (value.gameTypes) {
      value.gameTypes.forEach((gameType, i) => tempGameTypesMap[gameType] = i);
    }

    this.aiRecommendedGames = {
      ...value,
      gameTypes: (value.gameTypes || []),
      gameTypesMap: tempGameTypesMap
    };
  }

  @action.bound
  private _setTrendingRecommendedGamesForPlayer(trendingRecommendedGamesForPlayer: {[key: number]: number[]}) {
    this._trendingGamesRecommendations = {
      gamesByBets: (trendingRecommendedGamesForPlayer && trendingRecommendedGamesForPlayer[LIST_TYPE_ID.BETS]) || [],
      gamesByActiveUsers: (trendingRecommendedGamesForPlayer && trendingRecommendedGamesForPlayer[LIST_TYPE_ID.ACTIVE_USERS]) || []
    }
  }

  @action.bound
  private _setAllAiRecommendedGameTypes(gameTypes: number[]) {
    gameTypes = gameTypes || [];
    // creates a map where the key is the game type and the value is the index of that game type in the array
    this._allAiRecommendedGameTypes = gameTypes.reduce((acc, curr, index) => {
      acc[curr] = index;
      return acc;
    }, {});
  }

  private _onOauthChange() {
    this._initalizeTrendingRecommendations();
    this._updateAiRecommendedGames();
  }

  _initalizeTrendingRecommendations() {
    if(this.services.user.isAuthorized) {
      this._trendingGamesRecommendations = null;
    }
   }

  private async _updateAiRecommendedGames(): Promise<void> {
    let aiRecommendationsData = await this.services.sdk.getAIRecommendations({filterOutRecentlyPlayedGames: true});

    this._setAiRecommendedGames({
      gameTypes: aiRecommendationsData.gameTypes,
      engine: aiRecommendationsData.engine,
      gameTypesMap: {},
      isInitialized: true
    });

    aiRecommendationsData = await this.services.sdk.getAIRecommendations({maxGames: 500, filterOutRecentlyPlayedGames: false});
    this._setAllAiRecommendedGameTypes(aiRecommendationsData.gameTypes || []);

    this._sendAIRecommendationAnalytics();
  }

  private _sendAIRecommendationAnalytics(): void {

    let analyticsObject = this.aiRecommendedGames.gameTypes.reduce((result, gameType, index) => {
      result[gameType] = index;
      return result;
    }, {});

    this.services.analytics.sendElasticInfoEvent({
      category: SEO_CATEGORIES.AI_GAMES_RECOMMENDATIONS,
      action: JSON.stringify(analyticsObject),
      label: this.aiRecommendedGames.engine,
      eventType:"Orbit_AI_Feature"
    }, true);
  }

  async getCasinoRecommendedDynamicListsForPlayer(listIDs: string[]): Promise<Record<number, number>> {
    return await this.services.api.insightController.getCasinoRecommendedDynamicListsForPlayer({
      listsIDs: listIDs
    });
  }

  sortGames(gameTypes: number[], options?: IAiSortOptions): number[] {
    const allAiRecommendedGameTypes = Check.isEmpty(options?.gamesMapOverride) ? this._allAiRecommendedGameTypes : options?.gamesMapOverride;
    if(!allAiRecommendedGameTypes || Object.keys(allAiRecommendedGameTypes).length === 0) {
      return gameTypes;
    }

    const ignoreNumberOfGames = Check.isNumber(options?.ignoreNumberOfGames) ? options.ignoreNumberOfGames : 0;
    let fixedGameTypes = ignoreNumberOfGames > 0 ? gameTypes.slice(0, ignoreNumberOfGames) : [];
    const aiGameTypes = ignoreNumberOfGames > 0 ? gameTypes.slice(ignoreNumberOfGames) : gameTypes;

    const originalIndexes = {};
    aiGameTypes.forEach((gt, index) => {
      originalIndexes[gt] = index;
    });

    aiGameTypes.sort((gt1, gt2) => {
      let index1 = allAiRecommendedGameTypes[gt1];
      let index2 = allAiRecommendedGameTypes[gt2];

      if(Check.isUndefined(index1) && Check.isUndefined(index2)) {
        return (originalIndexes[gt1] < originalIndexes[gt2]) ? -1 : 1;
      }

      if(Check.isUndefined(index1)) { //if gt1 is not in the AI recommendations
        return 1;
      } else if(Check.isUndefined(index2)) {
        return -1;
      }

      return (index1 < index2) ? -1 : 1; //gt1 goes before gt2 if index1 < index2
    });

    // concatenate fixed game types (not sorted) with ai sorted ones
    return fixedGameTypes.concat(aiGameTypes);
  }

  getAiEngine(): string | undefined {
    return this.aiRecommendedGames.engine;
  }

  getAiEngineForGame(gameType: number): string | undefined {
    return this.aiRecommendedGames.gameTypesMap[gameType] === undefined ? undefined : this.aiRecommendedGames.engine;
  }

  private _isLoadTrendingRecommendedGamesForPlayerInProgress = false;
  private async _loadTrendingRecommendedGamesForPlayer(): Promise<void> {
    if(this._isLoadTrendingRecommendedGamesForPlayerInProgress) {
      return;
    }

    if(!Check.isNullOrUndefined(this._trendingGamesRecommendations)) {
      return;
    }

    this._isLoadTrendingRecommendedGamesForPlayerInProgress = true;
    try {
      const trendingRecommendedGames: { [key: number]: number[] } = await this.services.sdk.getTrendingRecommendedGamesForPlayer(LIST_TYPE_ID.BETS, LIST_TYPE_ID.ACTIVE_USERS);
      this._setTrendingRecommendedGamesForPlayer(trendingRecommendedGames);
    } finally {
      this._isLoadTrendingRecommendedGamesForPlayerInProgress = false;
    }

  }

  //  List Type can be either 1 or 2, when 1 is sorted by bets and 2 is sorted by active_users
  private _getGamesTypesByListTypeID(listTypeId?: LIST_TYPE_ID): number[] {

    if(!Check.isNullOrUndefined(listTypeId)) {
      if (listTypeId === LIST_TYPE_ID.BETS) {
        return this.trendingRecommendedGamesForPlayerByBets;
      } else if(listTypeId === LIST_TYPE_ID.ACTIVE_USERS) {
        return this.trendingRecommendedGamesForPlayerByActiveUsers;
      } else {
        this.services.logger.error(`Fail to get ai games order from api. wrong listTypeId: ${listTypeId}`);
      }
    }

    return [];
  }

   sortGamesByAI(gamesTypesArray: number[], listTypeId: LIST_TYPE_ID) {
    const aiGamesByScore = this._getGamesTypesByListTypeID(listTypeId);
     if(!aiGamesByScore) {
       return gamesTypesArray;
     }
     
    return gamesTypesArray.sort((first,second) => {
      const firstGamePlace = aiGamesByScore.indexOf(first);
      const secondGamePlace = aiGamesByScore.indexOf(second);
      if(firstGamePlace === -1 || secondGamePlace === -1) {
        if(firstGamePlace === -1) {
          return secondGamePlace === -1 ? 0 : 1;
        } else {
          return -1;
        }
      }

      return firstGamePlace - secondGamePlace;
    });

  }
}


